import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import NativeChip, { ChipSize } from "@galaxy-io/dls/chips/Chip";
import Alert, { AlertVariant } from "@galaxy-io/dls/feedback/Alert";
import { InputSize } from "@galaxy-io/dls/inputs/Input";
import Text, { TextSize, TextVariant } from "@galaxy-io/dls/text/Text";
import { t } from "@galaxy-io/dls/theme/tokens/t";
import Widget, { WidgetVariant } from "@galaxy-io/dls/widget/Widget";
import {
  type RecordFilterGroup,
  type RecordFilterNode,
  type RecordRule,
  recordFilterCount,
} from "@gtm/contracts/core/record-filter";
import type { SavedFilter, SavedFilterKind } from "@gtm/contracts/core/saved-filters";
import { styled } from "@linaria/react";
import { PlusIcon, XIcon } from "@phosphor-icons/react";
import { useQueryClient } from "@tanstack/react-query";
import { type ComponentProps, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ConfirmAction } from "@/components/workspace/confirm-action";
import { FilterPopover } from "@/components/workspace/filter-popover";
import { SavedFilters } from "@/components/workspace/saved-filters";
import { MultiSelectField, SelectField } from "@/components/workspace/select-field";
import { apiClient, unwrap } from "@/lib/api-client";

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: ${t.space[8]};
  min-width: 0;
  flex-wrap: wrap;
`;
const Stack = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${t.space[12]};
  min-width: 0;
`;
const ConditionRow = styled.div`
  display: grid;
  grid-template-columns: 104px minmax(0, 1fr);
  align-items: end;
  gap: ${t.space[12]};
  @media (max-width: 650px) {
    grid-template-columns: minmax(0, 1fr);
    > :first-child { max-width: 104px; }
    gap: ${t.space[8]};
  }
`;
const Connector = styled.div`
  min-height: 32px;
  display: flex;
  align-items: center;
  padding-left: ${t.space[8]};
`;
const Rule = styled.div<{ $valueWidth: number }>`
  display: grid;
  grid-template-columns: 112px 132px ${({ $valueWidth }) => `${$valueWidth}px`} 28px;
  align-items: end;
  gap: ${t.space[8]};
  min-width: 0;
  @media (max-width: 850px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) 28px;
    > [data-filter-value] { grid-column: 1 / 3; grid-row: 2; }
    > button { grid-column: 3; grid-row: 1; }
  }
`;
const Value = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${t.space[4]};
  > input, > div { flex: 1; min-width: 90px; }
`;
const Grow = styled.div`
  flex: 1;
  min-width: 0;
`;
const Chips = styled.span`
  display: flex;
  align-items: center;
  gap: ${t.space[4]};
  min-width: 0;
  overflow: hidden;
`;
const empty = (): RecordFilterGroup => ({ operator: "and", rules: [] });
const newRule = (field: RecordRule["field"] = "status"): RecordRule => {
  if (field === "date") return { field, operator: "within", value: "7", unit: "days" };
  if (field === "text" || field === "domain") return { field, operator: "contains", value: "" };
  return { field, operator: "is", values: [] };
};
export type FilterField = { value: RecordRule["field"]; label: string };
type Options = {
  fields: FilterField[];
  choices: Partial<Record<RecordRule["field"], ComponentProps<typeof MultiSelectField>["options"]>>;
  loading: boolean;
  error: boolean;
};

function RuleEditor({
  rule,
  onChange,
  onRemove,
  options,
}: {
  rule: RecordRule;
  onChange: (rule: RecordRule) => void;
  onRemove: () => void;
  options: Options;
}) {
  const relative =
    rule.field === "date" && (rule.operator === "within" || rule.operator === "older");
  const operators =
    rule.field === "date"
      ? [
          { value: "within", label: "Within the last" },
          { value: "older", label: "Older than" },
          { value: "before", label: "Before" },
          { value: "after", label: "On or after" },
          { value: "on", label: "On date" },
          { value: "between", label: "Between dates" },
        ]
      : rule.field === "text" || rule.field === "domain"
        ? [
            { value: "contains", label: "Contains" },
            { value: "not_contains", label: "Does not contain" },
          ]
        : [
            { value: "is", label: "Is any of" },
            { value: "is_not", label: "Is none of" },
          ];
  const choices = options.choices[rule.field] ?? [];
  return (
    <Rule
      $valueWidth={
        "values" in rule ? 240 : rule.field === "date" && rule.operator === "between" ? 280 : 200
      }
      role="group"
      aria-label="Filter condition"
    >
      <SelectField
        compact
        width="100%"
        label="Field"
        value={rule.field}
        onChange={(value) => onChange(newRule(value as RecordRule["field"]))}
        options={options.fields}
      />

      <SelectField
        compact
        width="100%"
        label="Condition"
        value={rule.operator}
        options={operators}
        onChange={(operator) => {
          if (rule.field === "date") {
            const nextRelative = operator === "within" || operator === "older";
            onChange({
              ...rule,
              operator: operator as typeof rule.operator,
              value: nextRelative === relative ? rule.value : nextRelative ? "1" : "",
              unit: rule.unit || "days",
            });
          } else onChange({ ...rule, operator } as RecordRule);
        }}
      />

      <Value
        data-filter-value
        title={rule.field === "date" && !relative ? "UTC · range includes both dates" : undefined}
      >
        {"values" in rule ? (
          <MultiSelectField
            renderSelectedContent={
              rule.values.length > 1
                ? undefined
                : (items, placeholder) =>
                    items.length ? (
                      <Chips>
                        {items.map((item) => (
                          <NativeChip key={item.id} label={item.label} size={ChipSize.SMALL} />
                        ))}
                      </Chips>
                    ) : (
                      <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
                        {placeholder}
                      </Text>
                    )
            }
            label="Values"
            width="100%"
            placeholder="Select value"
            value={rule.values.join(",")}
            disabled={rule.field !== "status" && (options.loading || options.error)}
            onChange={(value) => onChange({ ...rule, values: value ? value.split(",") : [] })}
            options={[
              ...choices,
              ...rule.values
                .filter((value) => !choices.some((choice) => choice.value === value))
                .map((value) => ({ value, label: value })),
            ]}
          />
        ) : rule.field === "text" || rule.field === "domain" ? (
          <Input
            size={InputSize.SMALL}
            aria-label="Text to match"
            placeholder="Enter text"
            value={rule.value}
            onChange={(e) => onChange({ ...rule, value: e })}
            label="Text to match"
          />
        ) : (
          <>
            <Input
              size={InputSize.SMALL}
              aria-label={
                relative
                  ? "Duration"
                  : rule.operator === "between"
                    ? "Start date (UTC)"
                    : "Date (UTC)"
              }
              type={relative ? "number" : "date"}
              min={relative ? "1" : undefined}
              step={relative ? "1" : undefined}
              value={rule.value}
              onChange={(e) => onChange({ ...rule, value: e })}
              label={
                relative
                  ? "Duration"
                  : rule.operator === "between"
                    ? "Start date (UTC)"
                    : "Date (UTC)"
              }
            />

            {relative && (
              <SelectField
                compact
                width="100%"
                label="Time unit"
                value={rule.unit || "days"}
                onChange={(unit) => onChange({ ...rule, unit: unit as typeof rule.unit })}
                options={["days", "weeks", "months", "years"].map((value) => ({
                  value,
                  label: value,
                }))}
              />
            )}
            {rule.operator === "between" && (
              <>
                <Text>to</Text>

                <Input
                  size={InputSize.SMALL}
                  aria-label="End date (UTC)"
                  type="date"
                  min={rule.value || undefined}
                  value={rule.end || ""}
                  onChange={(e) => onChange({ ...rule, end: e })}
                  label="End date (UTC)"
                />
              </>
            )}
          </>
        )}
      </Value>
      <Button
        size={ButtonSize.SMALL}
        ariaLabel="Remove condition"
        icon={XIcon}
        variant={ButtonVariant.TERTIARY}
        onClick={onRemove}
        tooltip="Remove condition"
      />
    </Rule>
  );
}

function GroupEditor({
  group,
  onChange,
  onRemove,
  options,
}: {
  group: RecordFilterGroup;
  onChange: (group: RecordFilterGroup) => void;
  onRemove?: () => void;
  options: Options;
}) {
  function replace(index: number, node?: RecordFilterNode) {
    onChange({
      ...group,
      rules: node
        ? group.rules.map((child, i) => (i === index ? node : child))
        : group.rules.filter((_, i) => i !== index),
    });
  }
  return (
    <Stack role="group" aria-label="Filter group">
      {group.rules.map((node, index) => (
        <ConditionRow key={index}>
          {index === 1 ? (
            <SelectField
              compact
              width="100%"
              label="Group match"
              value={group.operator}
              onChange={(operator) => onChange({ ...group, operator: operator as "and" | "or" })}
              options={[
                { value: "and", label: "and" },
                { value: "or", label: "or" },
              ]}
            />
          ) : (
            <Connector>
              <Text variant={TextVariant.SECONDARY}>{index === 0 ? "Where" : group.operator}</Text>
            </Connector>
          )}
          {"rules" in node ? (
            <Widget variant={WidgetVariant.SECONDARY}>
              <GroupEditor
                group={node}
                options={options}
                onChange={(next) => replace(index, next)}
                onRemove={() => replace(index)}
              />
            </Widget>
          ) : (
            <RuleEditor
              rule={node}
              options={options}
              onChange={(next) => replace(index, next)}
              onRemove={() => replace(index)}
            />
          )}
        </ConditionRow>
      ))}
      <Row>
        <Button
          size={ButtonSize.SMALL}
          label="Add condition"
          icon={PlusIcon}
          variant={ButtonVariant.SECONDARY}
          onClick={() =>
            onChange({ ...group, rules: [...group.rules, newRule(options.fields[0]?.value)] })
          }
        />
        <Button
          size={ButtonSize.SMALL}
          label="Add group"
          icon={PlusIcon}
          variant={ButtonVariant.TERTIARY}
          onClick={() =>
            onChange({
              ...group,
              rules: [
                ...group.rules,
                { operator: "or", rules: [newRule(options.fields[0]?.value)] },
              ],
            })
          }
        />
        {onRemove && (
          <>
            <Grow />
            <Button
              size={ButtonSize.SMALL}
              ariaLabel="Remove group"
              icon={XIcon}
              variant={ButtonVariant.TERTIARY}
              onClick={onRemove}
              tooltip="Remove group"
            />
          </>
        )}
      </Row>
    </Stack>
  );
}

export function RecordFilterBuilder({
  kind,
  applied,
  invalid = false,
  fields,
  choices,
  optionsLoading,
  optionsError,
  onRetry,
  validate,
  onApply,
}: {
  kind: SavedFilterKind;
  applied: RecordFilterGroup;
  invalid?: boolean;
  fields: FilterField[];
  choices: Options["choices"];
  optionsLoading: boolean;
  optionsError: boolean;
  onRetry?: () => void;
  validate: (group: RecordFilterGroup) => boolean;
  onApply: (group: RecordFilterGroup) => void;
}) {
  const client = useQueryClient();
  const [pending, setPending] = useState<SavedFilter | null>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<RecordFilterGroup>(empty);
  const options = { fields, choices, loading: optionsLoading, error: optionsError };
  return (
    <>
      <FilterPopover
        open={open}
        onOpenChange={(next) => {
          if (next) setDraft(applied);
          setOpen(next);
        }}
        count={recordFilterCount(applied)}
        invalid={invalid}
        disabled={!!draft.rules.length && !validate(draft)}
        onReset={() => setDraft(empty())}
        onApply={() => {
          onApply(draft);
          setOpen(false);
        }}
      >
        {invalid && (
          <Alert variant={AlertVariant.ERROR}>
            {
              <>
                <Text>Invalid filter. Reset to start again.</Text>
              </>
            }
          </Alert>
        )}
        {optionsError && (
          <Row>
            <Text>Filter values unavailable.</Text>
            <Button label="Retry" variant={ButtonVariant.SECONDARY} onClick={onRetry} />
          </Row>
        )}
        {optionsLoading && <Text>Loading filter values…</Text>}
        <GroupEditor group={draft} onChange={setDraft} options={options} />
        {open && (
          <SavedFilters
            kind={kind}
            draft={draft}
            valid={!!draft.rules.length && validate(draft)}
            onLoad={setDraft}
            onDelete={setPending}
          />
        )}
      </FilterPopover>
      <ConfirmAction
        isOpen={!!pending}
        header="Delete saved filter"
        label="Delete filter"
        isDestructive
        description={
          <>Delete “{pending?.name}” from the workspace? Applied conditions will stay in place.</>
        }
        onOpenChange={(next) => {
          if (!next) setPending(null);
        }}
        onConfirm={async () => {
          if (!pending) return;
          await unwrap(
            apiClient().DELETE("/saved-filters/{id}", { params: { path: { id: pending.id } } }),
          );
          client.setQueryData<SavedFilter[]>(["saved-filters", kind], (items) =>
            items?.filter((item) => item.id !== pending.id),
          );
          setPending(null);
          void client.invalidateQueries({ queryKey: ["saved-filters", kind] });
          setOpen(true);
        }}
      />
    </>
  );
}
